import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { X, Users, Target, ListTodo, ArrowRight } from 'lucide-react';

export default function WelcomeBanner({ onNavigate, showLeads = true, orgType }) {
  const { user, token } = useAuth();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    const key = `qindex_welcome_dismissed_${user?.id}`;
    const wasDismissed = localStorage.getItem(key);
    if (!wasDismissed) setDismissed(false);
  }, [user?.id]);

  const dismiss = () => {
    localStorage.setItem(`qindex_welcome_dismissed_${user?.id}`, 'true');
    setDismissed(true);
  };

  if (dismissed) return null;

  // SFOs already have their single family — drop the "Add your first client"
  // step entirely. They land on My Home and can jump to the client overview
  // via the Clients tab.
  const isSfo = orgType === 'sfo';

  const steps = [
    ...(isSfo
      ? [{ icon: Users, label: 'View your family', action: () => onNavigate?.('clients'), color: 'bg-[#4A3B32] text-white' }]
      : [{ icon: Users, label: 'Add your first client', action: () => onNavigate?.('clients'), color: 'bg-[#4A3B32] text-white' }]),
    { icon: ListTodo, label: 'Create a task', action: () => onNavigate?.('tasks'), color: 'bg-blue-600 text-white' },
    ...(showLeads ? [{ icon: Target, label: 'Explore the pipeline', action: () => onNavigate?.('leads'), color: 'bg-emerald-600 text-white' }] : []),
  ];

  return (
    <div className="rounded-xl border border-[#D4A574]/40 bg-gradient-to-r from-[#FAF5F0] to-[#F5EDE5] p-5 relative" data-testid="welcome-banner">
      <button onClick={dismiss} className="absolute top-3 right-3 p-1 rounded-lg hover:bg-[#E6E0D9] transition-colors" data-testid="welcome-dismiss-btn">
        <X className="w-4 h-4 text-[#8C8078]" />
      </button>
      <h3 className="font-heading text-base text-[#2C2420] mb-1">
        Welcome to QIndex One, {user?.name?.split(' ')[0] || 'there'}
      </h3>
      <p className="text-sm text-[#5D524D] mb-4">Here are three quick steps to get started:</p>
      <div className="flex flex-wrap gap-2">
        {steps.map((step, idx) => (
          <Button
            key={idx}
            onClick={() => { step.action(); dismiss(); }}
            className={`h-9 text-sm px-4 gap-2 ${step.color}`}
            data-testid={`welcome-step-${idx}`}
          >
            <step.icon className="w-4 h-4" />
            {step.label}
            <ArrowRight className="w-3 h-3" />
          </Button>
        ))}
      </div>
    </div>
  );
}
